import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import DOMPurify from 'dompurify';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import ErrorHandler from '@Root/ErrorHandler';
import {
    IoTrashBin,
    IoMail,
    IoCheckmarkCircle,
    IoCloseCircle,
    IoAlertCircle,
    IoFolderOpen,
    IoPeople,
    IoBusiness,
} from 'react-icons/io5';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './DeleteAccountPage.scss';

export default function DeleteAccountPage() {
    const [deleteText, setDeleteText]                     = useState('');
    const [emailId, setEmailId]                           = useState('');
    const [deleteOptionDisabled, setDeleteOptionDisabled] = useState(true);
    const [projectOwnerPermissions, setProjectOwnerPermissions] = useState([]);
    const [teamOwnerPermissions, setTeamOwnerPermissions]       = useState([]);
    const [orgOwnerPermissions, setOrgOwnerPermissions]         = useState([]);
    const [errorMessage, setErrorMessage]   = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [isDeleting, setIsDeleting]       = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const userAuthService = new UserAuthService();
    const errorHandler    = new ErrorHandler();
    const { activeUserId, activeUser } = useReduxActiveUser();
    const className = 'deleteAccountPage';

    useEffect(() => { renderData(); }, []);

    const renderData = async () => {
        try {
            let res = await userAuthService.getUserOwnerPermissionList(activeUserId);
            res = JSON.parse(res.body);
            const projects = res.projects || [];
            const teams    = res.teams    || [];
            const orgs     = res.organizations || [];
            setProjectOwnerPermissions(projects);
            setTeamOwnerPermissions(teams);
            setOrgOwnerPermissions(orgs);
            setDeleteOptionDisabled(!!(projects.length || teams.length || orgs.length));
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const handleDeleteTextChange = (e) => {
        setDeleteText(DOMPurify.sanitize(e.target.value));
        setShowErrorMessage(false);
    };

    const handleEmailTextChange = (e) => {
        setEmailId(DOMPurify.sanitize(e.target.value));
        setShowErrorMessage(false);
    };

    const handleDeleteClick = () => {
        if (deleteText !== 'Delete my account') return;
        if (emailId === activeUser.email) {
            setShowConfirmation(true);
        } else {
            setShowErrorMessage(true);
            setErrorMessage('Email does not match your account.');
        }
    };

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);
            await userAuthService.deleteUser({ id: activeUserId, emailId });
            userAuthService.logout();
        } catch (err) {
            setShowErrorMessage(true);
            setErrorMessage('Failed to delete account. Please try again.');
            setIsDeleting(false);
        }
    };

    const isFormValid = deleteText === 'Delete my account' && emailId.length > 2 && !deleteOptionDisabled;
    const totalOwned  = (projectOwnerPermissions?.length || 0) +
                        (teamOwnerPermissions?.length || 0) +
                        (orgOwnerPermissions?.length || 0);

    const ownershipGroups = [
        { label: 'Projects',      icon: <IoFolderOpen />, items: projectOwnerPermissions },
        { label: 'Teams',         icon: <IoPeople />,     items: teamOwnerPermissions   },
        { label: 'Organizations', icon: <IoBusiness />,   items: orgOwnerPermissions    },
    ].filter(g => g.items?.length);

    return (
        <div className={className}>
            <div className={`${className}__card`}>

                {/* ── Title ──────────────────────────────────────────── */}
                <div className={`${className}__head`}>
                    <div className={`${className}__head__icon`}>
                        <IoTrashBin />
                    </div>
                    <div>
                        <h2 className={`${className}__head__title`}>Delete Account</h2>
                        <p className={`${className}__head__sub`}>
                            Permanently removes your account and all associated data.
                            This action cannot be undone.
                        </p>
                    </div>
                </div>

                <div className={`${className}__divider`} />

                <div className={`${className}__refund-warning`}>
                    <IoAlertCircle className={`${className}__refund-warning__icon`} />
                    <span>
                        Subscription payments are non-refundable. If you delete your account, you will not receive a refund for your current subscription period.
                    </span>
                </div>

                {/* ── Ownership blocker ──────────────────────────────── */}
                {deleteOptionDisabled && (
                    <div className={`${className}__blocker`}>
                        <div className={`${className}__blocker__alert`}>
                            <IoAlertCircle className={`${className}__blocker__alert__icon`} />
                            <span>
                                You own <strong>{totalOwned} {totalOwned === 1 ? 'item' : 'items'}</strong>.
                                Transfer or delete them before proceeding.
                            </span>
                        </div>

                        <div className={`${className}__blocker__groups`}>
                            {ownershipGroups.map(({ label, icon, items }) => (
                                <div key={label} className={`${className}__blocker__group`}>
                                    <div className={`${className}__blocker__group__label`}>
                                        {icon} {label} ({items.length})
                                    </div>
                                    <div className={`${className}__blocker__group__list`}>
                                        {items.map((item, i) => (
                                            <div key={i} className={`${className}__blocker__group__item`}>
                                                <span className={`${className}__blocker__group__item__tag`}>{item.tag}</span>
                                                <span className={`${className}__blocker__group__item__name`}>{item.title}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Confirmation form ──────────────────────────────── */}
                <div className={`${className}__form${deleteOptionDisabled ? ' --disabled' : ''}`}>
                    <p className={`${className}__form__hint`}>
                        Type <span className={`${className}__form__phrase`}>Delete my account</span> and
                        confirm your email to enable deletion.
                    </p>

                    <div className={`${className}__field`}>
                        <label className={`${className}__field__label`}>Confirmation phrase</label>
                        <TextField
                            placeholder="Delete my account"
                            value={deleteText}
                            onChange={handleDeleteTextChange}
                            disabled={deleteOptionDisabled}
                            variant="outlined"
                            fullWidth
                            error={deleteText !== '' && deleteText !== 'Delete my account'}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IoTrashBin className={`${className}__field__adornment`} />
                                    </InputAdornment>
                                ),
                                endAdornment: deleteText === 'Delete my account' && (
                                    <InputAdornment position="end">
                                        <IoCheckmarkCircle className={`${className}__field__check`} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '10px', backgroundColor: 'var(--color-muiBackground)' },
                            }}
                        />
                        {deleteText !== '' && deleteText !== 'Delete my account' && (
                            <span className={`${className}__field__error`}>
                                <IoCloseCircle /> Type exactly: "Delete my account"
                            </span>
                        )}
                    </div>

                    <div className={`${className}__field`}>
                        <label className={`${className}__field__label`}>Email address</label>
                        <TextField
                            placeholder={activeUser?.email || 'your@email.com'}
                            value={emailId}
                            onChange={handleEmailTextChange}
                            disabled={deleteOptionDisabled}
                            variant="outlined"
                            fullWidth
                            error={showErrorMessage}
                            type="email"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IoMail className={`${className}__field__adornment`} />
                                    </InputAdornment>
                                ),
                                endAdornment: emailId === activeUser?.email && emailId && (
                                    <InputAdornment position="end">
                                        <IoCheckmarkCircle className={`${className}__field__check`} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '10px', backgroundColor: 'var(--color-muiBackground)' },
                            }}
                        />
                        {showErrorMessage && (
                            <span className={`${className}__field__error`}>
                                <IoCloseCircle /> {errorMessage}
                            </span>
                        )}
                    </div>

                    <button
                        type="button"
                        className={`${className}__delete-btn`}
                        onClick={handleDeleteClick}
                        disabled={!isFormValid || isDeleting}
                    >
                        {isDeleting ? (
                            <><div className={`${className}__spinner`} />Deleting…</>
                        ) : (
                            <><IoTrashBin />Delete My Account</>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Final confirmation modal ───────────────────────────── */}
            {showConfirmation && (
                <div className={`${className}__overlay`} onClick={() => !isDeleting && setShowConfirmation(false)}>
                    <div className={`${className}__modal`} onClick={e => e.stopPropagation()}>
                        <div className={`${className}__modal__icon`}>
                            <IoTrashBin />
                        </div>
                        <h3 className={`${className}__modal__title`}>Delete account?</h3>
                        <p className={`${className}__modal__body`}>
                            All your data will be permanently deleted and cannot be recovered.
                        </p>
                        <p className={`${className}__modal__refund`}>
                            Subscription payments are non-refundable.
                        </p>
                        <div className={`${className}__modal__actions`}>
                            <button
                                className={`${className}__modal__cancel`}
                                onClick={() => setShowConfirmation(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className={`${className}__modal__confirm`}
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting
                                    ? <><div className={`${className}__spinner`} />Deleting…</>
                                    : 'Yes, delete forever'
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
